/**
 * nurturing.ts — service layer for the Nurturing engine (Engine 4).
 *
 * nurturing_sequences (the shell) holds the DEFINITION. This adds enrollment:
 *   - enrollContact / enrollAudience: put contacts into a sequence (step 0).
 *   - listEnrollments / unenroll: read status / manual exit.
 *   - computeAdvance: PURE step-advance logic — the heart of the worker.
 *
 * The scheduled `nurture-worker` Edge Function advances enrollments and sends
 * each step via the SHARED `_shared/deliverEmail` (stubbed until Resend is
 * configured) — there is no separate send path. The worker keeps an identical
 * copy of `computeAdvance` (Deno can't import from src/).
 *
 * Exit conditions (MVP): finished all steps OR manual unenroll. Reply-detection
 * is a deferred follow-up (needs inbound-email parsing).
 *
 * Requires migration 0007 (nurture_enrollments).
 */

import { supabase } from './supabase';
import type { AudienceFilter } from './marketing';

export interface SequenceStep {
  delay_days: number;
  subject: string;
  body: string;
}

export interface NurtureEnrollment {
  id: string;
  sequence_id: string;
  contact_id: string | null;
  email: string;
  current_step: number;
  status: 'active' | 'completed' | 'exited';
  next_send_at: string | null;
  last_sent_at: string | null;
  enrolled_at: string;
  created_at: string;
}

export interface AdvanceResult {
  /** The step to send NOW (null if nothing to send / already finished). */
  send: SequenceStep | null;
  /** The enrollment state after this tick. */
  next: { current_step: number; status: 'active' | 'completed'; next_send_at: string | null };
}

const DAY_MS = 86_400_000;

/**
 * PURE: given the current step index, the sequence's steps, and `now`, decide
 * which step to send and the next enrollment state. `now` is a PARAMETER (never
 * read inside) so this is deterministic + unit-testable. The nurture-worker
 * keeps an identical copy.
 *
 *   - current_step in range  → send steps[current_step]; advance to the next
 *     step (active, due now + nextStep.delay_days) or 'completed' if it was last.
 *   - current_step out of range → nothing to send, already 'completed'.
 */
export function computeAdvance(currentStep: number, steps: SequenceStep[], now: Date): AdvanceResult {
  const idx = currentStep ?? 0;
  const list = Array.isArray(steps) ? steps : [];
  if (idx >= list.length) {
    return { send: null, next: { current_step: idx, status: 'completed', next_send_at: null } };
  }
  const send = list[idx];
  const nextIdx = idx + 1;
  if (nextIdx >= list.length) {
    return { send, next: { current_step: nextIdx, status: 'completed', next_send_at: null } };
  }
  const delayDays = Number(list[nextIdx]?.delay_days) || 0;
  return {
    send,
    next: {
      current_step: nextIdx,
      status: 'active',
      next_send_at: new Date(now.getTime() + delayDays * DAY_MS).toISOString(),
    },
  };
}

/** When step 0 becomes due, relative to enrolment (step 0's own delay). */
function firstSendAt(steps: SequenceStep[]): string {
  const delay = Number(steps?.[0]?.delay_days) || 0;
  return new Date(Date.now() + delay * DAY_MS).toISOString();
}

/**
 * Enroll one contact into a sequence at step 0. Loads the contact's email and
 * the sequence's steps. Best-effort: returns the enrollment id, or null (logged)
 * if the contact has no email / the sequence is missing or has no steps.
 */
export async function enrollContact(sequenceId: string, contactId: string): Promise<string | null> {
  // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
  const { data: contact } = await supabase
    .from('crm_contacts')
    .select('id, email, workspace_id')
    .eq('id', contactId)
    .maybeSingle();
  const email = (contact as any)?.email;
  if (!email) {
    console.warn('[nurturing] enrollContact: contact has no email', contactId);
    return null;
  }

  // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
  const { data: seq } = await supabase
    .from('nurturing_sequences')
    .select('id, steps, workspace_id')
    .eq('id', sequenceId)
    .maybeSingle();
  const steps = ((seq as any)?.steps ?? []) as SequenceStep[];
  if (!seq || steps.length === 0) {
    console.warn('[nurturing] enrollContact: sequence missing or has no steps', sequenceId);
    return null;
  }

  const { data, error } = await supabase
    .from('nurture_enrollments')
    .insert({
      workspace_id: (seq as any).workspace_id ?? (contact as any).workspace_id ?? null,
      sequence_id: sequenceId,
      contact_id: contactId,
      email,
      current_step: 0,
      status: 'active',
      next_send_at: firstSendAt(steps),
    })
    .select('id')
    .single();
  if (error) {
    console.warn('[nurturing] enrollContact insert failed', error.message);
    return null;
  }
  return (data as { id: string }).id;
}

/**
 * Enroll a segment of contacts ('all' | 'tags') into a sequence. Reuses the same
 * crm_contacts resolution shape as the campaign sender. Returns the number
 * enrolled. ('list' segments are not supported yet — flagged follow-up.)
 */
export async function enrollAudience(sequenceId: string, audience: AudienceFilter): Promise<number> {
  // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
  const { data: seq } = await supabase
    .from('nurturing_sequences')
    .select('id, steps, workspace_id')
    .eq('id', sequenceId)
    .maybeSingle();
  const steps = ((seq as any)?.steps ?? []) as SequenceStep[];
  if (!seq || steps.length === 0) return 0;

  // The sequence's OWN workspace, with no fallback. There used to be one — it read the
  // hardcoded "company one". A sequence with no workspace is a data bug, and enrolling its
  // contacts under a guessed company would spread that bug rather than surface it.
  const wsId = (seq as any).workspace_id as string | null;
  if (!wsId) {
    console.warn(`[nurturing] sequence ${sequenceId} has no workspace — not enrolling anyone`);
    return 0;
  }
  // eslint-disable-next-line no-restricted-syntax -- error handling verified in surrounding code
  const { data: contacts } = await supabase
    .from('crm_contacts')
    .select('id, email, tags')
    .eq('workspace_id', wsId);

  let list = ((contacts ?? []) as any[]).filter((c) => c.email && String(c.email).includes('@'));
  if (audience?.type === 'tags' && Array.isArray(audience.tags) && audience.tags.length) {
    const want = audience.tags;
    list = list.filter((c) => Array.isArray(c.tags) && c.tags.some((t: string) => want.includes(t)));
  }
  if (list.length === 0) return 0;

  const due = firstSendAt(steps);
  const rows = list.map((c) => ({
    workspace_id: wsId,
    sequence_id: sequenceId,
    contact_id: c.id,
    email: c.email,
    current_step: 0,
    status: 'active',
    next_send_at: due,
  }));
  const { error } = await supabase.from('nurture_enrollments').insert(rows);
  if (error) {
    console.warn('[nurturing] enrollAudience insert failed', error.message);
    return 0;
  }
  return rows.length;
}

/** Enrollments for a sequence (newest first). */
export async function listEnrollments(sequenceId: string): Promise<NurtureEnrollment[]> {
  const { data, error } = await supabase
    .from('nurture_enrollments')
    .select('*')
    .eq('sequence_id', sequenceId)
    .eq('is_deleted', false)
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('[nurturing] listEnrollments failed', error.message);
    return [];
  }
  return (data ?? []) as NurtureEnrollment[];
}

/** Manual unenroll (exit condition): stop advancing this enrollment. */
export async function unenroll(enrollmentId: string): Promise<void> {
  await supabase
    .from('nurture_enrollments')
    .update({ status: 'exited', updated_at: new Date().toISOString() })
    .eq('id', enrollmentId);
}
